import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Highcharts from 'highcharts';
import 'highcharts/highcharts-more'; // Needed for polar charts
import 'highcharts/modules/accessibility'; // Import for side effects only, do not call

function SpiderChartComponent({ className, categories, series, tickInterval }) {
  const containerRef = useRef(null);
  useEffect(() => {
    const myChart = {
      chart: {
        polar: true,
        type: 'area',
        backgroundColor: 'transparent',
      },
      title: { text: '' },
      pane: {
        size: '90%',
      },
      xAxis: {
        categories,
        tickmarkPlacement: 'on',
        lineWidth: 0,
        accessibility: {
          description: `Axis shows six druglikeness parameters: ${categories.join(', ')}.`,
        },
      },
      yAxis: {
        visible: true,
        tickInterval,
        gridLineInterpolation: 'polygon',
        lineWidth: 0,
        min: 0,
        labels: { enabled: false },
      },
      tooltip: {
        headerFormat: '',
        shared: false,
        pointFormat: '{point.category} -  {point.y}',
      },
      legend: {
        enabled: true,
        symbolRadius: 0,
        itemMarginBottom: 10,
        itemStyle: {
          fontFamily: 'NexusSans,"Helvetica Neue",Helvetica,Arial,sans-serif',
          fontWeight: 400,
          fontSize: '12px',
          color: '#505050',
        },
      },
      accessibility: {
        enabled: true,
        pointDescriptionEnabled: true,
        keyboardNavigation: {
          enabled: true,
          focusBorder: {
            enabled: true,
          },
        },
        point: {
          describeNull: 'No data',
          valueDescriptionFormat: '{point.category}, value {point.y}.',
        },
        description:
          'This druglikeness radar chart compares a chemical substance to typical drug-like guidelines defined by Lipinski and Veber rules. Higher similarity indicates better drug-like properties.',
        landmarkVerbosity: 'all',
        screenReaderSection: {
          beforeChartFormat:
            '<h3>Druglikeness Chart</h3>' +
            `<p>This chart shows how close the tested substance is to drug-likeness norms across six molecular features: ${categories.join(', ')}.</p>`,
        },
      },
      plotOptions: {
        series: {
          events: {
            legendItemClick() {
              if (!this.visible) {
                return false;
              }
              const visibleCount = this.chart?.series?.reduce((total, serie) => total + serie.visible, 0);
              // Prevent hiding the last visible series
              return visibleCount > 1;
            },
          },
          stickyTracking: true,
          enableMouseTracking: true, // Override individual series enableMouseTracking settings
          marker: {
            enabled: true,
          },
          accessibility: {
            point: {
              valueDescriptionFormat: '{index}. {xDescription}, value {point.y}.',
            },
          },
        },
      },
      credits: { enabled: false },
      series,
    };
    Highcharts.chart(containerRef.current, myChart);
  }, [categories, series, tickInterval]);

  return <div className={className} ref={containerRef} />;
}

SpiderChartComponent.propTypes = {
  categories: PropTypes.array.isRequired,
  series: PropTypes.array.isRequired,
  tickInterval: PropTypes.number.isRequired,
  className: PropTypes.string,
};

export default SpiderChartComponent;
